package net.frozenbit.games.soundgame.audio

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.launch
import soundgame.app.shared.generated.resources.Res
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds
import kotlin.time.TimeSource

class MusicManager(
    private val audioPlayer: AudioPlayer,
    coroutineScope: CoroutineScope,
) {
    // Durations for fading in and out music when changing tracks
    private val attackTime = 800.milliseconds
    private val releaseTime = 1.seconds
    private val musicVolume = 1.0

    // Durations and volume for ducking music when a sound effect is played
    private val duckAttackTime = 700.milliseconds
    private val duckReleaseTime = 600.milliseconds
    private val duckedVolume = 0.25

    private var masterVolume = 0.8
    private var currentVolume = musicVolume

    private data class MusicChangeCommand(
        val fileName: String,
        val loop: Boolean,
        val position: Double,
    )

    private val commandQueue = MutableStateFlow<MusicChangeCommand?>(null)

    private val musicChangeJob = coroutineScope.launch {
        commandQueue.collectLatest { command ->
            if (command != null) {
                fadeVolume(0.0, releaseTime)
                println("[music] Changing track to ${command.fileName}, loop=${command.loop}, position=${command.position}")
                audioPlayer.prepareMusic(Res.getUri("files/${command.fileName}"), loop = command.loop)
                setVolume(0.0)
                audioPlayer.seekMusic(command.position)
                audioPlayer.resumeMusic()
                fadeVolume(musicVolume, attackTime)
            } else {
                println("[music] Pausing music")
                fadeVolume(0.0, releaseTime)
                audioPlayer.pauseMusic()
            }
        }
    }

    private val isDucked = MutableStateFlow(false)

    private val duckingJob = coroutineScope.launch {
        isDucked.collectLatest { ducked ->
            if (ducked) {
                fadeVolume(duckedVolume, duckAttackTime)
            } else {
                fadeVolume(musicVolume, duckReleaseTime)
            }
        }
    }

    fun setMasterVolume(volume: Double) {
        masterVolume = volume.coerceIn(0.0, 1.0)
        setVolume(currentVolume)
    }

    private fun setVolume(newVolume: Double) {
        currentVolume = newVolume
        audioPlayer.setMusicVolume(newVolume * masterVolume)
    }

    private suspend fun fadeVolume(
        targetVolume: Double,
        duration: Duration,
    ) {
        val timeSource = TimeSource.Monotonic
        val startTime = timeSource.markNow()
        val initialVolume = currentVolume
        val tickTime = 16.milliseconds
        while (true) {
            val elapsed = startTime.elapsedNow()
            if (elapsed >= duration) {
                setVolume(targetVolume)
                break
            }
            val progress = (elapsed / duration).coerceIn(0.0, 1.0)
            val newVolume = initialVolume + (targetVolume - initialVolume) * progress
            setVolume(newVolume)
            delay(tickTime)
        }
    }

    fun setMusicTrack(fileName: String, loop: Boolean = false, position: Double = 0.0) {
        commandQueue.value = MusicChangeCommand(fileName, loop, position)
    }

    fun pauseMusic() {
        commandQueue.value = null
    }

    fun getMusicPosition(): Double {
        return audioPlayer.getMusicPosition()
    }

    fun duck() {
        isDucked.value = true
    }

    fun unduck() {
        isDucked.value = false
    }
}

suspend fun MusicManager.withDuck(block: suspend () -> Unit) {
    duck()
    try {
        block()
    } finally {
        unduck()
    }
}
